// Client-side mirror of the server's platform-weighted credit costs
// (netlify/functions/_lib/billing.ts → PLATFORM_CREDIT_COST). Kept in lockstep
// so the composer's pre-publish credit gate matches what the backend actually
// charges — a mismatch would let a post look affordable and then be refused, or
// vice-versa. If you change one, change the other.
//
// Costs are deliberately non-round and weighted by each platform's real
// cost/scarcity to us: Meta (IG/FB) is cheapest, then TikTok and YouTube (scarce
// shared quotas), and X the priciest (billed per post).
export const PLATFORM_CREDIT_COST: Record<string, number> = {
  instagram: 38,
  facebook: 38,
  tiktok: 52,
  youtube: 71,
  x: 124,
};

export const DEFAULT_CREDIT_COST = 38;

/** Credits one publish to `platform` costs. Unknown platform → cheapest tier. */
export function creditCostForPlatform(platform: string): number {
  return PLATFORM_CREDIT_COST[platform] ?? DEFAULT_CREDIT_COST;
}

/** Total credits to publish to every platform id in `platforms`. */
export function creditCostForPlatforms(platforms: Iterable<string>): number {
  let total = 0;
  for (const id of platforms) total += creditCostForPlatform(id);
  return total;
}

/** Human-friendly credit count, e.g. 33000 → "33,000". */
export function formatCredits(n: number): string {
  return n.toLocaleString();
}

// Balance thresholds for the dashboard's low-balance warning. Grounded in the
// costs above: a full 5-platform post is 323 credits, so "critical" is under
// ~1.5 full posts (about to be stuck) and "low" is a handful of posts left.
export const CREDITS_CRITICAL = 500;
export const CREDITS_LOW = 1500;

export type CreditLevel = "ok" | "low" | "critical";

/** Severity band for a credit balance, for warning colours. */
export function creditLevel(balance: number): CreditLevel {
  if (balance < CREDITS_CRITICAL) return "critical";
  if (balance < CREDITS_LOW) return "low";
  return "ok";
}
